import {
  type PropsWithChildren,
  type Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Flex } from '../../../layouts/flex/Flex.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import type { ChartDatapoint } from '../../core/types/chart-data.js';
import { getFormatter } from '../../core/utils/get-formatter.js';
import { makeAccessor } from '../../core/utils/makeAccessor.js';
import type { XYChartRef } from '../../xy-chart/types/xy-chart.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { FlameIsolationNav } from '../shared/components/isolation-nav/FlameIsolationNav.js';
import { IsolateSegmentAction } from '../shared/components/tooltip/IsolateSegmentAction.js';
import {
  DEFAULT_FLAME_GRAPH_UNIT,
  DEPTH_FIELD,
  DISPLAY_NAME_FLAME_GRAPH,
  FLAME_GRAPH_HEIGHT_OVERHEAD_PX,
  FRAME_HEIGHT_PX,
  VALUE_FIELD,
  XY_DEFAULTS,
} from '../shared/constants.js';
import { scrollContainer } from '../shared/Flame.sty.js';
import { useFlameData } from '../shared/hooks/use-flame-data.js';
import { useFlameHeight } from '../shared/hooks/use-flame-height.js';
import { useFlameIsolateSpan } from '../shared/hooks/use-flame-isolate-span.js';
import { useFlameOnEvent } from '../shared/hooks/use-flame-on-event.js';
import { useFlameIsolationState } from '../shared/hooks/use-isolation-state.js';
import { FlameGraphTooltipSlot as Tooltip } from '../shared/slots/Tooltip.js';
import type { FlameGraphProps, FlameRef } from '../shared/types/flame-chart.js';
import { aggregateSpansToFlameGraph } from '../shared/utils/aggregate-spans-to-flame-graph.js';
import { resolveFlameTooltipContent } from '../shared/utils/resolve-flame-tooltip-content.js';
import { DefaultTooltip } from './components/tooltip/DefaultTooltip.js';
import { iterateSlots } from './utils/iterate-slots.js';

/**
 * @internal
 */
function FlameGraphRoot<TData extends ChartDatapoint = ChartDatapoint>(
  {
    data,
    idAccessor,
    parentIdAccessor,
    valueAccessor,
    selfValueAccessor,
    labelAccessor,
    sortAccessor,
    colorPalette,
    orientation = 'bottom-up',
    truncationMode,
    formatter,
    unit,
    currentZoom,
    seriesActions,
    height,
    loading,
    __globalActions,
    onClick,
    children,
  }: PropsWithChildren<FlameGraphProps<TData>>,
  ref: Ref<FlameRef>,
) {
  const isolationEnabled = __globalActions === true;
  const { isolatedId, isolate, goBack, goForward, canGoBack, canGoForward } =
    useFlameIsolationState();

  const resolvedFormatter = useMemo(() => getFormatter(formatter), [formatter]);

  const {
    isolatedRoot,
    effectiveData,
    visibleRootId,
    childrenByParentId,
    byId,
  } = useFlameData({
    data,
    idAccessor,
    parentIdAccessor,
    isolatedId,
    isolationEnabled,
  });

  const isolateSpan = useFlameIsolateSpan({
    isolationEnabled,
    byId,
    isolatedId,
    isolate,
  });

  useImperativeHandle(ref, () => ({ isolate: isolateSpan }), [isolateSpan]);

  const aggregatedData = useMemo(
    () =>
      aggregateSpansToFlameGraph(effectiveData, {
        idAccessor,
        parentIdAccessor,
        labelAccessor,
        valueAccessor,
        selfValueAccessor,
        sortAccessor,
      }),
    [
      effectiveData,
      idAccessor,
      parentIdAccessor,
      labelAccessor,
      valueAccessor,
      selfValueAccessor,
      sortAccessor,
    ],
  );

  const depthCount = useMemo(() => {
    let max = 0;
    for (const d of aggregatedData) {
      const depth = Number(d[DEPTH_FIELD] ?? 0);
      if (depth > max) {
        max = depth;
      }
    }
    return aggregatedData.length === 0 ? 0 : max + 1;
  }, [aggregatedData]);

  const { computedHeight, shouldScroll, scrollSectionHeight } = useFlameHeight(
    height,
    depthCount,
    FLAME_GRAPH_HEIGHT_OVERHEAD_PX,
  );

  const handleOnEvent = useFlameOnEvent({
    onClick,
    data,
    idAccessor,
    childrenByParentId,
  });

  const { customTooltipTemplate, colorRules } = iterateSlots<TData>(children);

  const xyRef = useRef<XYChartRef>(null);
  const handleScroll = useCallback(() => {
    xyRef.current?._clearTooltip();
  }, []);

  const rootValue = useMemo(
    () =>
      aggregatedData.reduce(
        (max, d) => Math.max(max, Number(d[VALUE_FIELD] ?? 0)),
        0,
      ),
    [aggregatedData],
  );

  const globalActions = useMemo(() => {
    if (!isolationEnabled) {
      return undefined;
    }
    return (datapoint: TData) => {
      const id = makeAccessor(idAccessor)(datapoint);
      return [
        <IsolateSegmentAction
          key="isolate"
          onSelect={() => isolateSpan(id)}
          disabled={id === visibleRootId}
        />,
      ];
    };
  }, [isolationEnabled, idAccessor, isolateSpan, visibleRootId]);

  const chart = (
    <XYChart
      ref={xyRef}
      data={aggregatedData}
      truncationMode={truncationMode}
      height={computedHeight}
      __onEvent={handleOnEvent}
      loading={loading}
    >
      <XYChart.RectSeries
        x0Accessor={XY_DEFAULTS.x0Accessor}
        x1Accessor={XY_DEFAULTS.x1Accessor}
        y0Accessor={DEPTH_FIELD}
        valueAccessor={VALUE_FIELD}
        colorPalette={colorPalette}
        _labelAccessor={labelAccessor}
        xAxisId={XY_DEFAULTS.xAxisId}
        yAxisId={XY_DEFAULTS.yAxisId}
      />
      <XYChart.XAxis
        id={XY_DEFAULTS.xAxisId}
        type="numerical"
        position="top"
        hidden
        currentZoom={isolatedRoot !== null ? undefined : currentZoom}
        hideCrosshair
      />
      <XYChart.YAxis
        id={XY_DEFAULTS.yAxisId}
        type="categorical"
        position="left"
        hidden
        reversed={orientation === 'bottom-up'}
        hideCrosshair
        _fixedBandSize={FRAME_HEIGHT_PX}
      />
      <XYChart.Legend hidden />
      <XYChart.Toolbar hidden />
      <XYChart.Select disabled />
      {colorRules}
      <XYChart.Tooltip>
        {(payload) =>
          resolveFlameTooltipContent(
            payload,
            data,
            customTooltipTemplate,
            () => (
              <DefaultTooltip<TData>
                payload={payload}
                data={data}
                labelAccessor={labelAccessor}
                selfValueAccessor={selfValueAccessor}
                formatter={resolvedFormatter}
                unit={unit ?? DEFAULT_FLAME_GRAPH_UNIT}
                seriesActions={seriesActions}
                rootValue={rootValue}
                globalActions={globalActions}
              />
            ),
          )
        }
      </XYChart.Tooltip>
      <XYChart.Select disabled />
    </XYChart>
  );
  const scrollableChart = (
    <section
      className={scrollContainer}
      style={{ height: scrollSectionHeight }}
      onScroll={handleScroll}
      aria-label="Scroll section for Flame Graph"
    >
      {chart}
    </section>
  );

  if (!isolationEnabled) {
    return shouldScroll ? scrollableChart : chart;
  }

  return (
    <Flex flexDirection="column" gap={8}>
      <FlameIsolationNav
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onGoBack={goBack}
        onGoForward={goForward}
      />
      {scrollableChart}
    </Flex>
  );
}

FlameGraphRoot.displayName = DISPLAY_NAME_FLAME_GRAPH;

/**
 * @internal
 */
export const FlameGraph = Object.assign(
  /* @__PURE__ */ forwardRef(FlameGraphRoot),
  {
    Tooltip,
    ColorRule,
  },
);

(FlameGraph as typeof FlameGraph & { displayName: string }).displayName =
  DISPLAY_NAME_FLAME_GRAPH;
